'use client';
import React, { useEffect, useCallback } from 'react';
import { InputBar } from '@/components/ui/InputBar';
import { Button } from '@/components/ui/Button';
import { IconSearch } from '@repo/svgs';
import { Popover } from '@/components/ui/Popover';
import PriceFilter from './filters/PriceFilter';
import BedroomFilter from './filters/BedroomFilter';
import { useStore } from '@/providers/StoreProvider';
import { cn } from '@/lib/cn';
import LocationFilter from './filters/location/LocationFilter';
import { useRouter } from 'next/navigation';
import { useBedroomFilter } from './filters/hooks/useBedroomFilter';
import { usePriceFilter } from './filters/hooks/usePriceFilter';
import { useAnalytics } from '@/hooks/useAnalytics';

type ActivePanel = 'price' | 'beds' | 'location' | null;

export default function ListingsSearchBar({
  initialActivePanel = null,
}: {
  initialActivePanel?: ActivePanel;
}) {
  const [activePanel, setActivePanel] = React.useState<ActivePanel>(initialActivePanel);
  // Sync with initialActivePanel when it changes
  React.useEffect(() => {
    if (initialActivePanel !== null) {
      setActivePanel(initialActivePanel);
    }
  }, [initialActivePanel]);

  const analytics = useAnalytics();
  const priceAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const bedsAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const locationAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const locationInputRef = React.useRef<HTMLInputElement | null>(null);
  const [locationSearchQuery, setLocationSearchQuery] = React.useState('');
  const neighborhoods = useStore(s => s.neighborhoods.neighborhoods);
  const listingsCount = useStore(s => s.listingsFilters.listingsCount);
  const isCountLoading = useStore(s => s.listingsFilters.isCountLoading);
  const updateListingsCount = useStore(s => s.listingsFilters.updateListingsCount);

  const openPanel = React.useCallback((panel: ActivePanel) => setActivePanel(panel), []);
  const closePanel = React.useCallback(() => setActivePanel(null), []);

  const { formatPriceDisplay } = usePriceFilter();
  const { formatBedroomsDisplay } = useBedroomFilter();
  const { fetchNeighborhoods } = useStore(s => s.neighborhoods);
  const router = useRouter();

  useEffect(() => {
    if (neighborhoods.length === 0) {
      fetchNeighborhoods();
    }
  }, []);

  // Selection label from store (handles parents and fully-selected boroughs)
  const selectionLabel = useStore(s => s.listingsFilters.getLocationSelectionLabel());
  const getApiFilterPayload = useStore(s => s.listingsFilters.getApiFilterPayload);
  const getUrlFilterPayload = useStore(s => s.listingsFilters.getUrlFilterPayload);
  const price = useStore(s => s.listingsFilters.price);
  const bedrooms = useStore(s => s.listingsFilters.bedrooms);
  const neighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const building = useStore(s => s.listingsFilters.building);
  const goToListings = useCallback(() => {
    analytics.track('header_listings_search', {
      type: 'home',
    });
    const urlPayload = getUrlFilterPayload();
    router.push(`/listings/${urlPayload}`);
  }, [router, price, bedrooms, neighborhoodIds]);
  // Location data and store handling is encapsulated inside NeighborhoodFilter

  // Common styles
  const baseDividerClasses =
    'divider border-l h-8 w-px border-tertiary transition-opacity [.input-bar-wrapper:hover+&]:opacity-0 [&:has(+.input-bar-wrapper:hover)]:opacity-0';
  const getInputBg = (panelType: ActivePanel) =>
    activePanel && activePanel !== panelType
      ? 'bg-secondary hover:bg-secondary-hover duration-0'
      : activePanel === panelType
        ? '!bg-primary duration-0 shadow-center'
        : 'bg-primary hover:bg-primary-hover duration-150';

  const getDividerClasses = (leftPanel: ActivePanel, rightPanel: ActivePanel) =>
    cn(
      baseDividerClasses,
      (activePanel === leftPanel || activePanel === rightPanel) && 'opacity-0',
    );

  // Focus input when location panel opens
  useEffect(() => {
    if (activePanel === 'location') {
      // Small delay to ensure the popover is rendered
      const timer = setTimeout(() => {
        locationInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activePanel]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLocationSearchQuery('');
    }, 300);
    return () => clearTimeout(timeout);
  }, [neighborhoodIds, building]);

  useEffect(() => {
    updateListingsCount({ withMapBoundaries: false });
  }, [price, bedrooms, neighborhoodIds, building]);

  return (
    <div
      className={cn(
        'border border-secondary flex items-center rounded-full overflow-hidden md:flex-wrap md:rounded-3xl lg:flex-nowrap lg:rounded-full',
        activePanel ? 'bg-secondary' : 'bg-primary shadow-lg',
      )}
    >
      <div
        ref={locationAnchorRef}
        className="input-bar-wrapper"
        onClick={() => {
          if (activePanel !== 'location') {
            openPanel('location');
          } else {
            // If panel is already open, focus the input
            locationInputRef.current?.focus();
          }
        }}
      >
        <InputBar
          ref={locationInputRef}
          label="Location"
          className={cn('w-80', getInputBg('location'))}
          placeholder="Neighborhood, address, or building"
          value={activePanel === 'location' ? locationSearchQuery : selectionLabel || ''}
          onChange={value => {
            setLocationSearchQuery(value);
            if (activePanel !== 'location') {
              openPanel('location');
            }
          }}
          state={activePanel === 'location' ? 'default' : 'readonly'}
          autoFocus={activePanel === 'location'}
        />
      </div>
      <div className={getDividerClasses('location', 'price')} />
      <div ref={priceAnchorRef} className="input-bar-wrapper" onClick={() => openPanel('price')}>
        <InputBar
          label="Price"
          state="readonly"
          className={cn('w-40 cursor-pointer', getInputBg('price'))}
          placeholder="Any price"
          value={formatPriceDisplay() || ''}
        />
      </div>
      <div className={getDividerClasses('price', 'beds')} />
      <div ref={bedsAnchorRef} className="input-bar-wrapper" onClick={() => openPanel('beds')}>
        <InputBar
          state="readonly"
          label="Bedrooms"
          className={cn('w-40 cursor-pointer', getInputBg('beds'))}
          placeholder="Any beds"
          value={formatBedroomsDisplay() || ''}
        />
      </div>
      <div className="p-2 w-full md:basis-full lg:basis-auto">
        <Button
          onClick={goToListings}
          className="flex items-center gap-2 md:w-full md:justify-center lg:w-auto"
          variant="primary"
          isLoading={isCountLoading}
          disabled={isCountLoading}
        >
          {!isCountLoading && <IconSearch className="w-4 h-4" />}
          <span className="text-body-2-high">
            {`Search ${listingsCount !== null ? listingsCount.toLocaleString() : ''} listings`}
          </span>
        </Button>
      </div>

      {/* Popover for Price panel */}
      <Popover
        open={activePanel === 'price'}
        onOpenChange={o => (o ? setActivePanel('price') : closePanel())}
        anchorRef={priceAnchorRef as React.RefObject<HTMLElement>}
        matchAnchorWidth={false}
        className="w-80"
      >
        <PriceFilter
          shouldUpdateUrl={false}
          requireConfirmation
          page="home"
          className="p-6 rounded-3xl bg-primary border border-tertiary shadow-lg"
        />
      </Popover>

      {/* Popover for Beds panel */}
      <Popover
        open={activePanel === 'beds'}
        onOpenChange={o => (o ? setActivePanel('beds') : closePanel())}
        anchorRef={bedsAnchorRef as React.RefObject<HTMLElement>}
        matchAnchorWidth={false}
      >
        <BedroomFilter
          showLabel={false}
          requireConfirmation
          page="home"
          className="p-6 rounded-3xl bg-primary border border-tertiary shadow-lg"
        />
      </Popover>

      {/* Popover for Location panel */}
      <Popover
        open={activePanel === 'location'}
        onOpenChange={o => (o ? setActivePanel('location') : closePanel())}
        anchorRef={locationAnchorRef as React.RefObject<HTMLElement>}
        matchAnchorWidth={false}
        className="w-120"
      >
        <LocationFilter
          searchQuery={locationSearchQuery}
          anchorRef={locationAnchorRef as React.RefObject<HTMLElement>}
          requireConfirmation={true}
          shouldUpdateUrl={false}
          primaryButtonText="Done"
        />
      </Popover>
    </div>
  );
}
